import { motion } from 'framer-motion';
import { StatType, STAT_LABELS } from '@/data/characters';
import { cn } from '@/lib/utils';

interface StatBadgeProps {
  stat: StatType;
  value?: number;
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
  highlight?: boolean;
  className?: string;
  showIcon?: boolean;
}

export function StatBadge({ 
  stat, 
  value, 
  size = 'md', 
  showValue = false,
  highlight = false,
  className,
  showIcon = false,
}: StatBadgeProps) {
  const statInfo = STAT_LABELS[stat] ?? {
    label: String(stat ?? 'STAT'),
    icon: typeof stat === 'string' ? stat.slice(0, 3).toUpperCase() : 'STAT',
  };
  const { label, icon } = statInfo;
  
  const sizeClasses = {
    sm: 'text-sm gap-1.5 px-2 py-1',
    md: 'text-base gap-2 px-3 py-2',
    lg: 'text-xl gap-3 px-4 py-3',
  };

  const iconSizes = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-3xl',
  };

  return (
    <motion.div
      className={cn(
        'inline-flex items-center justify-center text-center rounded-xl font-display font-semibold',
        'bg-secondary border border-border shrink-0',
        highlight && 'border-primary',
        sizeClasses[size],
        className
      )}
      initial={highlight ? { scale: 0.8, opacity: 0 } : undefined}
      animate={highlight ? { scale: 1, opacity: 1 } : undefined}
      transition={{ type: 'spring', duration: 0.5 }}
    >
      {showIcon && (
        <span className={cn('stat-icon flex items-center justify-center min-w-[2.25rem] text-center', iconSizes[size])}>
          {icon}
        </span>
      )}
      <span className="text-foreground leading-tight text-center whitespace-nowrap">{label}</span>
      {showValue && value !== undefined && (
        <span className="font-mono font-bold text-primary ml-2">
          {value.toLocaleString()}
        </span>
      )}
    </motion.div>
  );
}
